import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Mail, Phone, Wallet, Clock3, FileText, FolderOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CandidateDocumentViewer } from "@/components/CandidateDocumentViewer";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";

// Mirrors the studio's own public brand pillars (Registry > Public Branding)
// so a "culture fit" score is anchored to values the studio actually holds
// itself to, not a generic rubric.
const CRITERIA = [
  { key: "craft", label: "Obsessive Craft", hint: "Sweats the details — finish, feel, precision." },
  { key: "candour", label: "Radical Candour", hint: "Direct and honest, doesn't sugarcoat feedback." },
  { key: "systems", label: "Systems Thinking", hint: "Sees how their work fits the bigger picture." },
  { key: "ownership", label: "Ownership over Ego", hint: "Takes responsibility, open to being wrong." },
];

const WORK_DAILY_OPTIONS = [
  { value: "yes", label: "Yes — would love this" },
  { value: "maybe", label: "Maybe — some reservations" },
  { value: "no", label: "No — not a fit" },
];

const APPLICATION_QUESTIONS = [
  { key: "why_join", label: "Why do you want to join Analogy Design?" },
  { key: "challenging_project", label: "What is your most challenging project to date?" },
  { key: "expertise", label: "What design/engineering micro-offer are you most expert in?" },
];

const EMPTY_RATINGS = { craft: 0, candour: 0, systems: 0, ownership: 0 };

function StarRating({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} className="p-0.5" data-testid={`${testIdPrefix}-star-${n}`} aria-label={`Rate ${n}`}>
          <Star size={17} className={n <= value ? "text-[#F54800] fill-[#F54800]" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-sm text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function ApplicationTab({ candidate, onOpenDoc }) {
  const answers = candidate.application_answers || {};
  const hasAnswers = APPLICATION_QUESTIONS.some((q) => answers[q.key]);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <InfoRow icon={Mail} label="Email" value={candidate.email} />
        <InfoRow icon={Phone} label="Phone" value={candidate.phone} />
        <InfoRow icon={Wallet} label="Current salary" value={candidate.current_salary != null ? `₹${Number(candidate.current_salary).toLocaleString("en-IN")}` : null} />
        <InfoRow icon={Wallet} label="Expected salary" value={candidate.expected_salary != null ? `₹${Number(candidate.expected_salary).toLocaleString("en-IN")}` : null} />
        <InfoRow icon={Clock3} label="Notice period" value={candidate.notice_period} />
      </div>
      <div>
        <Label className="label-overline">Cover note</Label>
        <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1.5" data-testid="app-tab-cover-note">
          {candidate.notes || <span className="text-slate-400 italic">No cover note provided.</span>}
        </p>
      </div>
      {hasAnswers && (
        <div className="border-t border-slate-200 pt-4 space-y-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Application Answers</div>
          {APPLICATION_QUESTIONS.map((q) => answers[q.key] && (
            <div key={q.key} data-testid={`app-answer-${q.key}`}>
              <div className="text-xs font-medium text-slate-500">{q.label}</div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{answers[q.key]}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 border-t border-slate-200 pt-4">
        {candidate.resume_path && (
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => onOpenDoc("resume")} data-testid="app-tab-resume-btn">
            <FileText size={13} className="mr-1.5" />Resume
          </Button>
        )}
        {candidate.portfolio_path && (
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => onOpenDoc("portfolio")} data-testid="app-tab-portfolio-btn">
            <FolderOpen size={13} className="mr-1.5" />Portfolio
          </Button>
        )}
      </div>
    </div>
  );
}

function EvaluationTab({ candidate, onSaved }) {
  const { user } = useAuth();
  const [ratings, setRatings] = useState(EMPTY_RATINGS);
  const [workDaily, setWorkDaily] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cf = candidate.culture_fit || {};
    setRatings({ ...EMPTY_RATINGS, ...(cf.ratings || {}) });
    setWorkDaily(cf.work_daily || "");
    setNotes(cf.notes || "");
  }, [candidate]);

  const overall = useMemo(() => {
    const vals = Object.values(ratings).filter((v) => v > 0);
    if (!vals.length) return 0;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
  }, [ratings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        culture_fit: {
          ratings, work_daily: workDaily, overall_score: overall, notes,
          evaluated_by: user?.id, evaluated_by_name: user?.name,
          evaluated_at: new Date().toISOString(),
        },
      };
      await api.patch(`/candidates/${candidate.id}`, payload);
      toast.success("Culture fit evaluation saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {CRITERIA.map((c) => (
        <div key={c.key} className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900">{c.label}</div>
            <div className="text-[11px] text-slate-500">{c.hint}</div>
          </div>
          <StarRating value={ratings[c.key] || 0} onChange={(v) => setRatings({ ...ratings, [c.key]: v })} testIdPrefix={`cf-${c.key}`} />
        </div>
      ))}
      <div className="border-t border-slate-200 pt-3">
        <Label className="label-overline">Would you want to work with them daily?</Label>
        <Select value={workDaily} onValueChange={setWorkDaily}>
          <SelectTrigger className="rounded-sm mt-1" data-testid="cf-work-daily"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {WORK_DAILY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="label-overline">Evaluator notes</Label>
        <Textarea rows={3} className="rounded-sm mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cf-notes" />
      </div>
      <div className="bg-orange-50 border border-orange-200 rounded-sm px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-slate-600">Overall Culture Fit Score</span>
        <span className="text-lg font-display font-bold text-[#F54800]" data-testid="cf-overall-score">{overall ? `${overall}/5` : "—"}</span>
      </div>
      {candidate.culture_fit?.evaluated_by_name && (
        <p className="text-[11px] text-slate-400">
          Last evaluated by {candidate.culture_fit.evaluated_by_name} on {new Date(candidate.culture_fit.evaluated_at).toLocaleDateString()}
        </p>
      )}
      <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="cf-save-btn">
        {saving ? "Saving…" : "Save evaluation"}
      </Button>
    </div>
  );
}

function DocumentsTab({ candidate, onOpenDoc }) {
  if (!candidate.resume_path && !candidate.portfolio_path && !candidate.project_path) {
    return <p className="text-sm text-slate-400 italic" data-testid="docs-tab-empty">No documents attached.</p>;
  }
  return (
    <div className="space-y-2">
      {candidate.resume_path && (
        <button type="button" onClick={() => onOpenDoc("resume")} className="w-full flex items-center gap-2.5 text-left border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-slate-700 hover:border-[#F54800]/50 hover:bg-orange-50/40" data-testid="docs-tab-resume-btn">
          <FileText size={15} className="text-[#F54800]" /> Resume
        </button>
      )}
      {candidate.portfolio_path && (
        <button type="button" onClick={() => onOpenDoc("portfolio")} className="w-full flex items-center gap-2.5 text-left border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-slate-700 hover:border-[#F54800]/50 hover:bg-orange-50/40" data-testid="docs-tab-portfolio-btn">
          <FolderOpen size={15} className="text-[#F54800]" /> Portfolio
        </button>
      )}
      {candidate.project_path && (
        <button type="button" onClick={() => onOpenDoc("project")} className="w-full flex items-center gap-2.5 text-left border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-slate-700 hover:border-[#F54800]/50 hover:bg-orange-50/40" data-testid="docs-tab-project-btn">
          <FolderOpen size={15} className="text-[#F54800]" /> Project sample
        </button>
      )}
    </div>
  );
}

/** Tabbed candidate review modal — Application / Evaluation / Documents.
 * Replaces the old single-purpose CultureFitDialog as the candidate card's
 * click target (Evaluation tab still saves the exact same culture_fit shape). */
export function CandidateDetailModal({ candidate, open, onClose, onSaved }) {
  const [tab, setTab] = useState("application");
  const [viewerDoc, setViewerDoc] = useState(null);
  const [drivePreview, setDrivePreview] = useState(null);

  useEffect(() => { if (open) setTab("application"); }, [open]);

  if (!candidate) return null;

  const openDoc = (kind) => {
    const path = candidate[`${kind}_path`];
    if (!path) return;
    const title = `${candidate.name} — ${kind === "resume" ? "Resume" : kind === "portfolio" ? "Portfolio" : "Project sample"}`;
    if (path.startsWith("http")) setDrivePreview({ url: path, title });
    else setViewerDoc({ path, title });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="candidate-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{candidate.name}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{candidate.role_applied} · {candidate.business_unit || "—"}</DialogDescription>
          </DialogHeader>
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="rounded-sm grid grid-cols-3">
              <TabsTrigger value="application" className="rounded-sm" data-testid="cand-tab-application">Application</TabsTrigger>
              <TabsTrigger value="evaluation" className="rounded-sm" data-testid="cand-tab-evaluation">Evaluation</TabsTrigger>
              <TabsTrigger value="documents" className="rounded-sm" data-testid="cand-tab-documents">Documents</TabsTrigger>
            </TabsList>
            <div className="mt-3 max-h-[55vh] overflow-y-auto pr-1">
              <TabsContent value="application"><ApplicationTab candidate={candidate} onOpenDoc={openDoc} /></TabsContent>
              <TabsContent value="evaluation"><EvaluationTab candidate={candidate} onSaved={onSaved} /></TabsContent>
              <TabsContent value="documents"><DocumentsTab candidate={candidate} onOpenDoc={openDoc} /></TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
      <CandidateDocumentViewer open={!!viewerDoc} path={viewerDoc?.path} title={viewerDoc?.title} onClose={() => setViewerDoc(null)} />
      <DriveLinkPreviewModal open={!!drivePreview} url={drivePreview?.url} title={drivePreview?.title} onClose={() => setDrivePreview(null)} />
    </>
  );
}
